/**
 * Settings › Details and brand (TMMPDM-0088): the partner's Details table, moved from the Overview
 * exactly as it rendered there — the brand cell is "Has its own brand", or the no-brand text with
 * Create brand; Edit… opens the tenant's edit dialog (the page's `tenantEditOpen`), wired as the
 * tenant overview wires it. With no tenant, the brand still shows and the tenant columns are "—".
 * TMMPDM-0025: Create brand (25 s; 200 and 201 alike; 502 outcome-unknown → re-read, never re-POST).
 */
import { useState } from 'react';
import { Alert, Box, Stack } from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { SubmitButton, useNotify } from '@/components/page';
import { adminApi } from '@/api/tmm/admin';
import { ApiError, errorCopy, isOutcomeUnknown } from '@/api/errors';
import type { ResellerDto } from '@/api/tmm/types';
import { EditTenantDialog } from '../tenants/EditTenantDialog';
import { PartnerDetailsSection } from './PartnerDetailsSection';
import type { usePartnerTenant } from './PartnerTenantPanel';

export function PartnerDetailsPanel({
  reseller,
  tenantLoad,
  tenantEditOpen,
  onTenantEditOpenChange,
  onUpdated,
  onReload,
  actionsBusy,
}: {
  reseller: ResellerDto;
  tenantLoad: ReturnType<typeof usePartnerTenant>;
  tenantEditOpen: boolean;
  onTenantEditOpenChange: (open: boolean) => void;
  onUpdated: (reseller: ResellerDto) => void;
  /** Re-read the reseller (after an unconfirmed write). */
  onReload: () => void;
  /** A header write is in flight. */
  actionsBusy: boolean;
}) {
  const { t } = useCopy();
  const notify = useNotify();
  const [actionError, setActionError] = useState<string | null>(null);
  const [outcomeUnknown, setOutcomeUnknown] = useState(false);
  const [tenantOutcomeUnknown, setTenantOutcomeUnknown] = useState(false);
  const [brandBusy, setBrandBusy] = useState(false);

  const handleCreateBrand = async () => {
    setBrandBusy(true);
    setActionError(null);
    setOutcomeUnknown(false);
    try {
      const updated = await adminApi.createResellerBrand(reseller.id);
      onUpdated(updated);
      notify.success(t('resellers.overview.brandCreated', 'Brand created.'));
    } catch (err) {
      if (isOutcomeUnknown(err)) {
        setOutcomeUnknown(true);
        onReload();
        return;
      }
      const code = err instanceof ApiError ? String(err.code) : '';
      setActionError(
        code === 'PRODUCT_HAS_NO_DEFAULT_BRAND'
          ? t(
              'resellers.overview.brandNoProductDefault',
              'The product has no default brand to copy. Set one in saas-admin, then try again.',
            )
          : code === 'BRAND_SLUG_GENERATION_FAILED'
            ? t(
                'resellers.overview.brandSlugFailed',
                "Couldn't generate a name for the new brand. Try again.",
              )
            : errorCopy(
                err,
                t,
                t('resellers.overview.brandCreateError', 'Could not create the brand.'),
              ),
      );
    } finally {
      setBrandBusy(false);
    }
  };

  const hasOwnBrand = reseller.defaultBrandId !== undefined && reseller.defaultBrandId !== null;
  // As on main: Details reads the tenant only once it has loaded without a failure.
  const tenant =
    tenantLoad.tenant !== null && !tenantLoad.loading && !tenantLoad.failure
      ? tenantLoad.tenant
      : null;

  return (
    <Box>
      {actionError ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      ) : null}
      {outcomeUnknown || tenantOutcomeUnknown ? (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          onClose={() => {
            setOutcomeUnknown(false);
            setTenantOutcomeUnknown(false);
          }}
        >
          {t(
            'common.outcomeUnknown',
            "We couldn't confirm the change. The screen has been reloaded — check before trying again.",
          )}
        </Alert>
      ) : null}
      <PartnerDetailsSection
        tenant={tenant}
        {...(tenant ? { onEdit: () => onTenantEditOpenChange(true) } : {})}
        brand={
          hasOwnBrand ? (
            t('resellers.overview.ownBrand', 'Has its own brand')
          ) : (
            <Stack
              direction="row"
              spacing={1}
              component="span"
              sx={{ alignItems: 'center', display: 'inline-flex', flexWrap: 'wrap' }}
            >
              <span>
                {t(
                  'resellers.overview.productBrand',
                  'No brand of its own — its tenants use the product brand.',
                )}
              </span>
              <SubmitButton
                size="small"
                variant="outlined"
                onClick={() => void handleCreateBrand()}
                busy={brandBusy}
                disabled={actionsBusy}
              >
                {t('resellers.overview.createBrand', 'Create brand')}
              </SubmitButton>
            </Stack>
          )
        }
      />
      {tenant ? (
        <EditTenantDialog
          open={tenantEditOpen}
          tenant={tenant}
          onClose={() => onTenantEditOpenChange(false)}
          onUpdated={(updated) => {
            onTenantEditOpenChange(false);
            setTenantOutcomeUnknown(false);
            tenantLoad.update(updated);
          }}
          onOutcomeUnknown={() => {
            onTenantEditOpenChange(false);
            setTenantOutcomeUnknown(true);
            tenantLoad.reload();
          }}
        />
      ) : null}
    </Box>
  );
}
